import { useEffect, useState } from "react";
import { jobBridgeApi, jobsApi } from "@/lib/api";
import { Warning, CheckCircle, ArrowsClockwise, ShieldCheck, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BridgeGateBanner({ job, compact = false, onOverride }) {
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    jobBridgeApi.check(job.id)
      .then((d) => { if (alive) setCheck(d); })
      .catch(() => { if (alive) setCheck(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [job.id, job.weight_lbs, job.asset_id]);

  if (loading) {
    return <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] py-1 flex items-center gap-1"><ArrowsClockwise size={10} className="animate-spin" /> Bridge check…</div>;
  }
  if (!check) return null;

  if (check.is_compliant) {
    return compact ? null : (
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#22c55e] py-1" data-testid={`bridge-ok-${job.job_number}`}>
        <CheckCircle size={10} weight="fill" /> Bridge OK
      </div>
    );
  }

  // Violation present
  const blocked = check.gate_mode === "block" && !check.has_override;
  const color = blocked ? "#ef4444" : "#ff8c00";

  const submitOverride = async () => {
    try {
      await jobsApi.update(job.id, {
        bridge_override: true,
        bridge_override_note: note.trim() || "Permit on file (acknowledged)",
      });
      toast.success("Override saved — dispatch unblocked");
      setOverrideOpen(false);
      onOverride?.();
    } catch (e) {
      toast.error("Failed to save override");
    }
  };

  const worst = check.bridge_result?.worst_group;

  return (
    <div
      className="border my-2 p-2 text-[11px]"
      style={{ borderColor: color, background: `${color}10` }}
      data-testid={`bridge-banner-${job.job_number}`}
    >
      <div className="flex items-start gap-2">
        <Warning size={14} weight="fill" style={{ color }} className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold uppercase tracking-[0.15em] text-[10px]" style={{ color }}>
            {blocked ? "Dispatch Blocked" : "Bridge Warning"}
          </div>
          <div className="text-[#a1a1aa] mt-0.5 leading-snug">
            {check.error
              ? check.error
              : worst
              ? <>Axles A{worst.from_axle}–A{worst.to_axle} over by <strong className="text-white">{worst.over_lbs.toLocaleString()} lbs</strong> per Federal Bridge Formula{check.template_name && <> · {check.template_name}</>}</>
              : "Federal Bridge violation"}
          </div>
          {check.has_override && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#22c55e] mt-1 flex items-center gap-1">
              <ShieldCheck size={10} weight="fill" /> Override on file
            </div>
          )}
          {blocked && !check.has_override && (
            <button
              onClick={() => setOverrideOpen(true)}
              className="mt-1.5 px-2 py-1 border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold"
              data-testid={`override-btn-${job.job_number}`}
            >
              Acknowledge & Override
            </button>
          )}
        </div>
      </div>

      {overrideOpen && (
        <div className="fixed inset-0 z-[3100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOverrideOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0d0d0d] border border-[#ef4444] w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#ef4444]" />
                <div className="font-heading font-black text-sm tracking-tight">Acknowledge override</div>
              </div>
              <button onClick={() => setOverrideOpen(false)} className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ef4444] flex items-center justify-center"><X size={12} /></button>
            </div>
            <div className="text-[11px] text-[#a1a1aa] mb-3 leading-relaxed">
              You are acknowledging that a valid <strong className="text-white">overweight permit</strong> is on file for this load. The override will be logged with your account.
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Permit number / notes…"
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-3 py-2 text-xs mb-3 min-h-[60px]"
              data-testid="override-note"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setOverrideOpen(false)} className="px-3 py-1.5 border border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ef4444] text-[10px] uppercase tracking-[0.2em] font-bold">
                Cancel
              </button>
              <button onClick={submitOverride} className="px-3 py-1.5 bg-[#ef4444] text-white hover:bg-[#dc2626] text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="override-confirm">
                Acknowledge & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
